import * as React from 'react';
import { Checkbox, Description, Label, NumberField, Switch } from '@heroui/react';
import type { QuestionnaireFormData } from './types';

const AVAILABLE_FIELDS = [
  { label: '姓名', value: 'name' },
  { label: '性别', value: 'gender' },
  { label: '手机号', value: 'mobile' },
  { label: '邮箱', value: 'email' },
] as const;

export interface SettingsSectionProps {
  settings: QuestionnaireFormData['settings'];
  disabled?: boolean;
  onSettingsChange: (next: QuestionnaireFormData['settings']) => void;
}

export function SettingsSection({ settings, disabled, onSettingsChange }: SettingsSectionProps) {
  const hasTimeLimit = settings.timeLimit !== null;
  const timeLimitMinutes = settings.timeLimit ? Math.floor(settings.timeLimit / 60) : 0;

  const isFieldSelected = (fieldValue: string) => settings.submitterInfoFields.some((f) => f.field === fieldValue);

  const toggleField = (fieldValue: string, checked: boolean) => {
    const field = AVAILABLE_FIELDS.find((f) => f.value === fieldValue);
    if (!field) return;
    let nextFields = [...settings.submitterInfoFields];
    if (checked) {
      if (!isFieldSelected(fieldValue)) {
        nextFields.push({
          field: fieldValue,
          label: field.label,
          type: fieldValue === 'email' ? 'email' : fieldValue === 'mobile' ? 'tel' : 'text',
          required: true,
        });
      }
    } else {
      nextFields = nextFields.filter((f) => f.field !== fieldValue);
    }
    onSettingsChange({ ...settings, submitterInfoFields: nextFields });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="space-y-0.5">
          <div className="text-sm font-medium text-foreground">是否允许匿名提交</div>
          <Description className="text-xs">允许未登录用户提交问卷</Description>
        </div>
        <Switch
          isSelected={settings.allowAnonymous}
          onChange={(v) => onSettingsChange({ ...settings, allowAnonymous: v })}
          isDisabled={disabled}
        >
          <Switch.Content>
            <Switch.Control>
              <Switch.Thumb />
            </Switch.Control>
          </Switch.Content>
        </Switch>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-0.5">
            <div className="text-sm font-medium text-foreground">是否收集提交者个人信息</div>
            <Description className="text-xs">收集非注册用户的个人信息</Description>
          </div>
          <Switch
            isSelected={settings.collectSubmitterInfo}
            onChange={(v) => onSettingsChange({ ...settings, collectSubmitterInfo: v })}
            isDisabled={disabled}
          >
            <Switch.Content>
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
            </Switch.Content>
          </Switch>
        </div>
        {settings.collectSubmitterInfo ? (
          <div className="ml-0 space-y-2 sm:ml-6">
            <div className="text-sm text-muted-foreground">请选择收集的字段</div>
            <div className="flex flex-wrap gap-4">
              {AVAILABLE_FIELDS.map((field) => (
                <label key={field.value} className="flex cursor-pointer items-center gap-2">
                  <Checkbox
                    isSelected={isFieldSelected(field.value)}
                    onChange={(next) => toggleField(field.value, next)}
                    isDisabled={disabled}
                  >
                    <Checkbox.Control>
                      <Checkbox.Indicator />
                    </Checkbox.Control>
                  </Checkbox>
                  <span className="text-sm">{field.label}</span>
                </label>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-0.5">
            <div className="text-sm font-medium text-foreground">是否限制答题时间</div>
            <Description className="text-xs">设置答题时间限制（分钟）</Description>
          </div>
          <Switch
            isSelected={hasTimeLimit}
            onChange={(c) =>
              onSettingsChange({
                ...settings,
                timeLimit: c ? (settings.timeLimit ?? 0) : null,
              })
            }
            isDisabled={disabled}
          >
            <Switch.Content>
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
            </Switch.Content>
          </Switch>
        </div>
        {hasTimeLimit ? (
          <div className="ml-0 flex flex-col gap-2 sm:ml-6">
            <Label className="text-sm text-muted-foreground">时间限制</Label>
            <div className="flex max-w-xs items-center gap-2">
              <NumberField
                value={timeLimitMinutes}
                onChange={(n) => {
                  const v = Number.isFinite(n) ? n : 0;
                  onSettingsChange({
                    ...settings,
                    timeLimit: v > 0 ? v * 60 : 0,
                  });
                }}
                minValue={0}
                isDisabled={disabled}
              >
                <NumberField.Group>
                  <NumberField.DecrementButton />
                  <NumberField.Input />
                  <NumberField.IncrementButton />
                </NumberField.Group>
              </NumberField>
              <span className="text-sm text-muted-foreground">分钟</span>
            </div>
          </div>
        ) : null}
      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="space-y-0.5">
          <div className="text-sm font-medium text-foreground">是否在提交后显示分数</div>
          <Description className="text-xs">提交后立即显示得分</Description>
        </div>
        <Switch
          isSelected={settings.showScore}
          onChange={(v) => onSettingsChange({ ...settings, showScore: v })}
          isDisabled={disabled}
        >
          <Switch.Content>
            <Switch.Control>
              <Switch.Thumb />
            </Switch.Control>
          </Switch.Content>
        </Switch>
      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="space-y-0.5">
          <div className="text-sm font-medium text-foreground">是否随机打乱题目顺序</div>
          <Description className="text-xs">每次显示时随机排列题目</Description>
        </div>
        <Switch
          isSelected={settings.randomizeQuestions}
          onChange={(v) => onSettingsChange({ ...settings, randomizeQuestions: v })}
          isDisabled={disabled}
        >
          <Switch.Content>
            <Switch.Control>
              <Switch.Thumb />
            </Switch.Control>
          </Switch.Content>
        </Switch>
      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="space-y-0.5">
          <div className="text-sm font-medium text-foreground">是否显示答题进度</div>
          <Description className="text-xs">显示当前答题进度条</Description>
        </div>
        <Switch
          isSelected={settings.showProgress}
          onChange={(v) => onSettingsChange({ ...settings, showProgress: v })}
          isDisabled={disabled}
        >
          <Switch.Content>
            <Switch.Control>
              <Switch.Thumb />
            </Switch.Control>
          </Switch.Content>
        </Switch>
      </div>
    </div>
  );
}
